import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { getRequest, postRequest } from 'api/apiClient';
import { DevicesByWorkflowType, IntegrationDataType, VideoDeviceType } from 'types/videoDeviceTypes';

interface AltaVideoState {
    videoDevices: VideoDeviceType[];
    devicesByWorkflow: Record<number, DevicesByWorkflowType>;
    altaVideoIntegrationData: IntegrationDataType[];
    isAltaVideoInstalled: boolean;
    isLoggedIn: boolean;
    thumbnailPresence: Record<string, Record<string, { SIGNIN?: boolean; SIGNOUT?: boolean }>>;
    thumbnailLoading: Record<string, Record<string, { SIGNIN?: number; SIGNOUT?: number }>>;
}

const initialState: AltaVideoState = {
    videoDevices: [],
    devicesByWorkflow: {},
    altaVideoIntegrationData: [],
    isAltaVideoInstalled: false,
    isLoggedIn: false,
    thumbnailPresence: {},
    thumbnailLoading: {},
};

// ACC-2026-1704: Removed performLogin, getBaseUrl, and loginInFlight
// Authentication now handled server-side via proxy endpoints

export const fetchAltaVideoIntegrationData = createAsyncThunk<{ integrationData: IntegrationDataType[]; isInstalled: boolean }, number>(
    'altaVideo/fetchAltaVideoIntegrationData',
    async (orgId) => {
        const res = await getRequest(`/orgs/${orgId}/integrationMsiAltaVideo`);
        const isInstalled = Array.isArray(res.data) && res.data.length > 0;
        return { integrationData: res.data, isInstalled };
    });

export const fetchDevicesByWorkflow = createAsyncThunk<DevicesByWorkflowType, { orgId: number; siteId: number }>(
    'altaVideo/fetchDevicesByWorkflow',
    async ({ orgId, siteId }) => {
        const res = await getRequest(`/orgs/${orgId}/sites/${siteId}/msiAltaVideoDevice`);
        return res.data;
    });

export const fetchVideoDevices = createAsyncThunk<VideoDeviceType[], number>(
    'altaVideo/fetchVideoDevices',
    async (orgId) => {
        const res = await getRequest(`/orgs/${orgId}/integrationMsiAltaVideo/videoDevices`);
        return res.data;
    }
);

// Use server-side proxy for video authentication
// Server manages all authentication - no credentials exposed to client
export const botLoginAltaVideo = createAsyncThunk<void, number, { state: RootState }>(
    'altaVideo/botLoginAltaVideo',
    async (orgId: number, { getState, rejectWithValue }) => {

        if (getState().altaVideo.isLoggedIn) {
            return;
        }

        try {
            const response = await postRequest(`/orgs/${orgId}/msiAltaVideo/session`, {});

            // Check if server successfully created video session
            if (!response?.data?.success) {
                return rejectWithValue('Video session creation failed');
            }
        } catch (error) {
            return rejectWithValue('Login failed');
        }
    }
)

// ACC-2026-1704: Logout no longer needed as session management is server-side
export const botLogoutAltaVideo = createAsyncThunk<void, void, { state: RootState }>(
    'altaVideo/botLogoutAltaVideo',
    async () => {
        // No-op: server handles session cleanup
        return;
    }
)

// ACC-2026-1704: Use server-side proxy for thumbnail retrieval
export const checkThumbnailPresence = createAsyncThunk<
    { visitId: string; visitorId: string; type: 'SIGNIN' | 'SIGNOUT'; has: boolean; deviceId: string },
    { visitId: string; visitorId: string; type: 'SIGNIN' | 'SIGNOUT'; time: number; deviceId: string; orgId: number },
    { state: RootState }
>('altaVideo/checkThumbnailPresence',
    async ({ visitId, visitorId, type, time, deviceId, orgId }, { getState, rejectWithValue }) => {
        try {
            const device = getState().altaVideo.videoDevices.find(d => d.guId === deviceId);
            if (!device || !device.sourceId?.length || device.errors.length) {
                return { visitId, visitorId, type, has: false, deviceId };
            }

            const data = await getRequest(
                `/orgs/${orgId}/msiAltaVideo/thumbnail`,
                {
                    deviceId: device.guId,
                    sourceId: device.sourceId[0],
                    time
                },
                { responseType: 'blob' }
            );

            if (data.size > 0) {
                return { visitId, visitorId, type, has: true, deviceId };
            }

            return { visitId, visitorId, type, has: false, deviceId };
        } catch {
            return rejectWithValue({ visitId, visitorId, type, deviceId });
        }
    }
);

const altaVideoSlice = createSlice({
    name: 'altaVideo',
    initialState,
    reducers: {
        resetAltaVideoForOrg(state) {
            state.videoDevices = [];
            state.devicesByWorkflow = {};
            state.altaVideoIntegrationData = [];
            state.isAltaVideoInstalled = false;
            state.isLoggedIn = false;
        },
        clearDevicesByWorkflow(state) {
            state.devicesByWorkflow = {};
        },
        resetThumbnailPresense(state) {
            state.thumbnailPresence = {};
        }

    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAltaVideoIntegrationData.fulfilled, (state, action) => {
                state.altaVideoIntegrationData = action.payload.integrationData;
                state.isAltaVideoInstalled = action.payload.isInstalled;

                if (!action.payload.isInstalled) {
                    state.videoDevices = [];
                    state.devicesByWorkflow = {};
                    state.isLoggedIn = false;
                }
            })
            .addCase(fetchAltaVideoIntegrationData.rejected, (state) => {
                state.isAltaVideoInstalled = false;
                state.videoDevices = [];
                state.devicesByWorkflow = {};
                state.altaVideoIntegrationData = [];
                state.isLoggedIn = false;
            })
            .addCase(fetchDevicesByWorkflow.fulfilled, (state, action: PayloadAction<DevicesByWorkflowType>) => {
                const siteId = action.payload.site.id;
                state.devicesByWorkflow[siteId] = action.payload;
            })
            .addCase(fetchVideoDevices.fulfilled, (state, action: PayloadAction<VideoDeviceType[]>) => {
                state.videoDevices = action.payload;
            })
            .addCase(botLoginAltaVideo.fulfilled, (state) => {
                state.isLoggedIn = true;
            })
            .addCase(botLoginAltaVideo.rejected, (state) => {
                state.isLoggedIn = false;
            })
            .addCase(botLogoutAltaVideo.fulfilled, (state) => {
                state.isLoggedIn = false;
            })
            .addCase(botLogoutAltaVideo.rejected, (state) => {
                state.isLoggedIn = true;
            })
            .addCase(checkThumbnailPresence.pending, (state, action) => {
                const { visitId, visitorId, type } = action.meta.arg;

                if (!state.thumbnailLoading[visitId]) {
                    state.thumbnailLoading[visitId] = {};
                }
                if (!state.thumbnailLoading[visitId][visitorId]) {
                    state.thumbnailLoading[visitId][visitorId] = {};
                }
                state.thumbnailLoading[visitId][visitorId][type] =
                    (state.thumbnailLoading[visitId][visitorId][type] ?? 0) + 1;
            })
            .addCase(checkThumbnailPresence.fulfilled, (state, action) => {
                const { visitId, visitorId, type, has } = action.payload;

                if (state.thumbnailLoading[visitId]?.[visitorId]) {
                    state.thumbnailLoading[visitId][visitorId][type] = Math.max(
                        0,
                        (state.thumbnailLoading[visitId][visitorId][type] ?? 0) - 1,
                    );
                }

                if (!state.thumbnailPresence[visitId]) {
                    state.thumbnailPresence[visitId] = {};
                }
                if (!state.thumbnailPresence[visitId][visitorId]) {
                    state.thumbnailPresence[visitId][visitorId] = {};
                }

                state.thumbnailPresence[visitId][visitorId][type] = has;
            })
            .addCase(checkThumbnailPresence.rejected, (state, action) => {
                const { visitId, visitorId, type } = action.meta.arg;

                if (state.thumbnailLoading[visitId]?.[visitorId]) {
                    state.thumbnailLoading[visitId][visitorId][type] = Math.max(
                        0,
                        (state.thumbnailLoading[visitId][visitorId][type] ?? 0) - 1,
                    );
                }

                if (!state.thumbnailPresence[visitId]) {
                    state.thumbnailPresence[visitId] = {};
                }
                if (!state.thumbnailPresence[visitId][visitorId]) {
                    state.thumbnailPresence[visitId][visitorId] = {};
                }
                state.thumbnailPresence[visitId][visitorId][type] = false;
            });
    },
});

export const { resetAltaVideoForOrg, clearDevicesByWorkflow, resetThumbnailPresense } = altaVideoSlice.actions;
export default altaVideoSlice.reducer;